import { useEffect, useState } from 'react';
import axios from 'axios';
import { ClockCounterClockwise } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const actionColors = {
  CREATE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  UPDATE: 'bg-amber-50 text-amber-700 border-amber-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  SEND_SMS_REMINDER: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  APPT_EMAIL_SEND: 'bg-blue-50 text-blue-700 border-blue-200',
  BATCH_CREATE_APPOINTMENTS: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  GENERATE_APPOINTMENTS: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

/**
 * Historial de auditoria completo de una cita (creacion, reprogramaciones,
 * cambios de estado, recordatorios, borrado...). Reutilizable desde la Agenda
 * o cualquier dialogo de cita. Requiere rol admin en backend.
 */
export default function AppointmentAuditTimeline({ appointmentId, open, onClose, lang = 'es' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !appointmentId) return;
    setLoading(true);
    setError(null);
    setData({ appointment_id: appointmentId, appointment: null, events: [] });
    axios.get(`${API_URL}/api/audit/appointments/${appointmentId}/timeline`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(err => {
        if (err?.response?.status === 403) {
          setError(lang === 'es' ? 'Solo los administradores pueden ver el historial de auditoria.' : 'Only admins can view the audit timeline.');
        } else {
          setError(lang === 'es' ? 'No se pudo cargar el historial.' : 'Could not load the timeline.');
        }
        setData({ appointment_id: appointmentId, appointment: null, events: [] });
      })
      .finally(() => setLoading(false));
  }, [open, appointmentId, lang]);

  const formatDate = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString(lang === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const actionLabel = (action) => {
    const map = {
      CREATE: lang === 'es' ? 'Cita creada' : 'Appointment created',
      UPDATE: lang === 'es' ? 'Cita modificada' : 'Appointment updated',
      DELETE: lang === 'es' ? 'Cita eliminada' : 'Appointment deleted',
      SEND_SMS_REMINDER: lang === 'es' ? 'Recordatorio SMS enviado' : 'SMS reminder sent',
      APPT_EMAIL_SEND: lang === 'es' ? 'Recordatorio por email enviado' : 'Email reminder sent',
      BATCH_CREATE_APPOINTMENTS: lang === 'es' ? 'Alta/reprogramacion por lote' : 'Batch create/reschedule',
      GENERATE_APPOINTMENTS: lang === 'es' ? 'Generacion automatica de citas' : 'Auto-generated appointments',
    };
    return map[action] || action;
  };

  // Lista de cambios "campo: antes -> despues" para eventos UPDATE.
  const renderChanges = (ev) => {
    if (ev.action !== 'UPDATE') return null;
    const nv = ev.new_values || {};
    const ov = ev.old_values || {};
    const keys = Object.keys(nv).filter(k => k !== 'updated_at');
    if (keys.length === 0) return null;
    const fmt = (v) => (v === undefined || v === null || v === '' ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));
    return (
      <div className="mt-1.5 space-y-1" data-testid="appt-audit-changes">
        {keys.map(k => (
          <div key={k} className="text-[11px] text-stone-600 flex flex-wrap items-center gap-1">
            <span className="font-medium text-stone-500">{k}:</span>
            <span className="font-mono text-stone-400 line-through">{fmt(ov[k])}</span>
            <span className="text-stone-400">→</span>
            <span className="font-mono text-stone-700">{fmt(nv[k])}</span>
          </div>
        ))}
      </div>
    );
  };

  const events = data?.events || [];
  // El backend devuelve DESC (reciente primero). Para contar el historial
  // "desde que se creo" mostramos ASC (cronologico ascendente).
  const baseOrdered = [...events].reverse();
  // Fallback para citas historicas (generadas antes del registro individual
  // de CREATE): si no hay ningun evento de creacion pero la cita existe con
  // created_at, mostramos una entrada derivada con la fecha de creacion del
  // registro para que SIEMPRE aparezca cuando se creo la cita.
  const hasCreateEvent = events.some(e => ['CREATE', 'GENERATE_APPOINTMENTS', 'BATCH_CREATE_APPOINTMENTS'].includes(e.action));
  const createdAt = data?.appointment?.created_at;
  const derivedCreate = (!hasCreateEvent && createdAt)
    ? { id: '__derived_create__', action: 'CREATE', timestamp: createdAt, user_email: null, _derived: true }
    : null;
  const ordered = derivedCreate ? [derivedCreate, ...baseOrdered] : baseOrdered;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="appt-audit-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClockCounterClockwise size={18} className="text-[#1f3e80]" />
            {lang === 'es' ? 'Historial de auditoria de la cita' : 'Appointment audit timeline'}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{appointmentId}</DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-xs text-amber-600 italic" data-testid="appt-audit-error">{error}</p>
        )}

        {data?.appointment && (
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs grid grid-cols-2 gap-2" data-testid="appt-audit-header">
            <div><span className="text-stone-400">{lang === 'es' ? 'Estudio' : 'Study'}: </span><span className="font-mono">{data.appointment.study_id || 'Qsalud'}</span></div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Voluntario' : 'Volunteer'}: </span>{data.appointment.volunteer_name || data.appointment.volunteer_id || '-'}</div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Visita' : 'Visit'}: </span>{data.appointment.visit_code || '-'}</div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Fecha/Hora' : 'Date/Time'}: </span>{data.appointment.date} {data.appointment.time}</div>
            <div><span className="text-stone-400">{lang === 'es' ? 'Estado' : 'Status'}: </span>{data.appointment.status || '-'}</div>
          </div>
        )}
        {data && !data.appointment && !loading && !error && (
          <p className="text-[11px] text-amber-600 italic">{lang === 'es' ? 'La cita ya no existe (eliminada). Se muestra solo el historial de auditoria.' : 'Appointment no longer exists (deleted). Showing audit history only.'}</p>
        )}

        {loading ? (
          <p className="text-sm text-stone-500">{lang === 'es' ? 'Cargando...' : 'Loading...'}</p>
        ) : ordered.length === 0 && !error ? (
          <p className="text-sm text-stone-400 italic">{lang === 'es' ? 'Sin eventos de auditoria para esta cita.' : 'No audit events for this appointment.'}</p>
        ) : (
          <ol className="relative border-l-2 border-stone-200 ml-2 space-y-4 py-1" data-testid="appt-audit-events">
            {ordered.map((ev, i) => (
              <li key={ev.id || i} className="ml-4 relative" data-testid={`appt-audit-event-${i}`}>
                <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full bg-[#1f3e80] border-2 border-white"></span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`text-[10px] font-mono ${actionColors[ev.action] || 'bg-stone-50 text-stone-600'}`}>{actionLabel(ev.action)}</Badge>
                  <span className="text-xs font-mono text-stone-500">{formatDate(ev.timestamp)}</span>
                </div>
                <p className="text-xs text-stone-700 mt-1">
                  <span className="text-stone-400">{lang === 'es' ? 'Por' : 'By'}: </span>
                  <span className="font-medium">{ev.user_email || (ev._derived ? (lang === 'es' ? 'No registrado (cita anterior al registro individual)' : 'Not recorded (legacy appointment)') : '-')}</span>
                </p>
                {ev._derived && (
                  <p className="text-[10px] text-stone-400 italic mt-0.5">
                    {lang === 'es' ? 'Fecha de creacion del registro de la cita.' : 'Appointment record creation date.'}
                  </p>
                )}
                {renderChanges(ev)}
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
